/**
 * UpgradeTree — modal tech-tree overlay. Each node is a compact ICON button laid
 * out on a (col,row) grid with SVG connectors to its prerequisites. Tapping a node
 * opens a detail popup (name / description / cost) with a Confirm button; tapping
 * outside the popup closes it so you can pick another. Owned = green, buyable =
 * lit blue, locked = dim.
 */
import { useState } from "react";
import {
  UPGRADES,
  UPGRADE_COLS,
  UPGRADE_ROWS,
  UPGRADES_BY_ID,
  nodeState,
  canPurchase,
  type UpgradeNode,
} from "@/game/upgrades/upgradeTree";
import { t, useLang } from "@/i18n";

const BASE = import.meta.env.BASE_URL;
const SPRITE = (f: string) => `${BASE}sprites/${f}`;

// Compact icon grid. Milestone nodes render a touch bigger so the hull sprites pop.
const COL_W = 86;
const ROW_H = 74;
const NODE = 52;
const MILE = 64;
const PAD = 16;

/** Icon for a node — a ship/weapon sprite where one fits, else a category glyph.
 * Repeated icons down a branch are intentional: they read as one line at a glance. */
function nodeIcon(id: string): { img?: string; glyph?: string } {
  // Hull milestones → their actual ship sprites.
  if (id === "mk2") return { img: SPRITE("MTS_2.png") };
  if (id === "mk3") return { img: SPRITE("MTS_3.png") };
  if (id === "mk4") return { img: SPRITE("MTS_4.png") };
  if (id === "mkv_bts") return { img: SPRITE("MTS_V_BTS.png") };
  if (id === "mkv_swl") return { img: SPRITE("MTS_V_SWL.png") };
  if (id === "mkvi_bts") return { img: SPRITE("MTS_VI_BTS.png") };
  if (id === "mkvi_swl") return { img: SPRITE("MTS_VI_SWL.png") };
  if (id === "mkvii_swl") return { img: SPRITE("MTS_VII_SWL.png") };
  // Mk VI Swarm-lord ability branches (specific icons, before the generic missile/fighter rules).
  if (id.startsWith("swlvi_missilecd")) return { glyph: "⏱️" };
  if (id === "swlvi_smartmissile") return { glyph: "🎯" };
  if (id === "swlvi_dronebeam") return { glyph: "🚀" };
  if (id === "swlvi_fmissile") return { glyph: "🚀" };
  if (id.startsWith("swlvi_fbay")) return { img: SPRITE("player_fighter.png") };
  if (id.startsWith("swlvi_speed")) return { glyph: "🚀" };
  // Mk VII secret worm hull: the refit uses the EOW head, segment upgrades use the body.
  if (id === "mkvii") return { img: SPRITE("EOW_Head.png") };
  if (id === "mkviii_rift") return { glyph: "🌀" }; // Time & Space Rift ability
  if (id === "mkviii_coil") return { glyph: "🌪️" }; // Maelstrom Coil ability
  if (id === "mkviii") return { glyph: "🔷" }; // mechanical worm (until EOW8 art lands)
  if (id.startsWith("mkvii_seg")) return { img: SPRITE("EOW_Body.png") };
  if (id.startsWith("mkvii_beam")) return { img: SPRITE("cannon_gun.png") }; // maw-beam upgrades
  if (id.startsWith("mkvii_speed")) return { glyph: "🚀" }; // worm speed
  if (id.includes("branch")) return { glyph: "💥" }; // branching missiles
  // Systems with a dedicated single sprite.
  if (id.startsWith("fleet")) return { img: SPRITE("player_fighter.png") };
  if (id.startsWith("btsrange")) return { img: SPRITE("turret_basic.png") };
  if (id.startsWith("mkvi_beam")) return { img: SPRITE("cannon_gun.png") }; // Big-Cannon beam upgrades
  // Category glyphs (drones.png is a spritesheet, so drones use an emoji).
  if (id.startsWith("laser")) return { glyph: "⛏" };
  if (id.startsWith("optic")) return { glyph: "🎯" };
  if (id.startsWith("beam")) return { glyph: "📡" };
  if (id.startsWith("solar")) return { glyph: "☀️" };
  if (id.startsWith("engine") || id.startsWith("mk4speed") || id.startsWith("btsspeed"))
    return { glyph: "🚀" };
  if (id.startsWith("refine")) return { glyph: "💎" };
  if (id.startsWith("hangar")) return { glyph: "🛰️" };
  if (
    id.startsWith("dspeed") ||
    id.startsWith("dcargo") ||
    id.startsWith("dmine") ||
    id.startsWith("drange")
  )
    return { glyph: "🛰️" };
  if (id.includes("dronebay")) return { glyph: "🐝" }; // Mk V + Mk VI Swarm-lord drone bays
  if (id.startsWith("mechbay") || id.startsWith("repair")) return { glyph: "🔧" };
  if (id.includes("missile")) return { glyph: "💥" }; // Swarm-lord pods + Mk VI silos
  if (id.startsWith("btshp")) return { glyph: "🛡️" };
  if (id === "omniMine") return { glyph: "🔄" };
  return { glyph: "◆" };
}

interface Props {
  purchased: string[];
  minerals: number;
  skillPoints: number;
  units: number;
  energyCores: number;
  inventory: Record<string, number>;
  atMechanical: boolean;
  highlightId?: string | null;
  onPurchase: (id: string) => void;
  onClose: () => void;
}

export default function UpgradeTree({
  purchased,
  minerals,
  skillPoints,
  units,
  energyCores,
  inventory,
  atMechanical,
  highlightId,
  onPurchase,
  onClose,
}: Props) {
  useLang();
  const owned = new Set(purchased);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const boardW = UPGRADE_COLS * COL_W + PAD * 2;
  const boardH = UPGRADE_ROWS * ROW_H + PAD * 2;

  const sizeOf = (n: UpgradeNode) => (n.milestone ? MILE : NODE);
  const cx = (n: UpgradeNode) => PAD + n.col * COL_W + COL_W / 2;
  const top = (n: UpgradeNode) => PAD + n.row * ROW_H + (ROW_H - sizeOf(n)) / 2;
  const bottom = (n: UpgradeNode) => top(n) + sizeOf(n);

  const buyableOf = (n: UpgradeNode) =>
    canPurchase(n, owned, minerals, skillPoints, units) &&
    (n.cores ?? 0) <= energyCores &&
    (n.location !== "mechanical" || atMechanical);

  // Secret refits (e.g. Mk VII) stay hidden until the player holds the required items —
  // and so does anything gated behind them, so the whole branch is a surprise.
  const hiddenOf = (n: UpgradeNode): boolean => {
    if (owned.has(n.id)) return false;
    if (n.hiddenUntil && !owned.has(n.hiddenUntil)) return true; // secret sequel: hidden until that node is owned
    if (
      n.requiresItems &&
      Object.entries(n.requiresItems).some(([id, c]) => (inventory[id] ?? 0) < c)
    ) {
      return true;
    }
    return n.requires.some((rid) => {
      const p = UPGRADES_BY_ID[rid];
      return p ? hiddenOf(p) : false;
    });
  };

  const selected = selectedId ? UPGRADES_BY_ID[selectedId] : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="upg-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Upgrade tree"
      >
        <div className="upg-head">
          <div className="upg-head-title">⚙ {t("Upgrade Tree")}</div>
          <div className="upg-head-minerals">
            <span className="cur-chip">
              <span className="upg-min-dot" /> {fmt(minerals)}{" "}
              <span className="cur-label">Minerals</span>
            </span>
            <span className="cur-chip sp">
              ★ {skillPoints} <span className="cur-label">Skill</span>
            </span>
            <span className="cur-chip u">
              ◆ {fmt(units)} <span className="cur-label">Units</span>
            </span>
            <span className="cur-chip core">
              ⬢ {energyCores} <span className="cur-label">Energy Cores</span>
            </span>
          </div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="upg-scroll">
          <div className="upg-board" style={{ width: boardW, height: boardH }}>
            <svg
              className="upg-lines"
              width={boardW}
              height={boardH}
              viewBox={`0 0 ${boardW} ${boardH}`}
            >
              {UPGRADES.flatMap((n) => {
                if (hiddenOf(n)) return [];
                return n.requires.map((rid) => {
                  const p = UPGRADES_BY_ID[rid];
                  if (!p || hiddenOf(p)) return null;
                  // Soft vertical-biased S-curve: the edge leaves the parent and enters the child
                  // straight down/up, so connectors dock cleanly instead of cutting diagonals.
                  const x1 = cx(p);
                  const y1 = bottom(p);
                  const x2 = cx(n);
                  const y2 = top(n);
                  const dy = y2 - y1;
                  const dir = dy >= 0 ? 1 : -1;
                  const k = Math.max(18, Math.abs(dy) * 0.5);
                  const d = `M ${x1} ${y1} C ${x1} ${y1 + dir * k}, ${x2} ${y2 - dir * k}, ${x2} ${y2}`;
                  // Only the SELECTED node's own links glow; everything else stays a calm lattice,
                  // so a fully-unlocked tree no longer reads as a wall of bright-blue string.
                  const activeEdge = selectedId != null && (selectedId === n.id || selectedId === rid);
                  const ownedEdge = owned.has(rid);
                  return (
                    <path
                      key={`${rid}->${n.id}`}
                      d={d}
                      fill="none"
                      strokeLinecap="round"
                      stroke={activeEdge ? "#5fd0ff" : ownedEdge ? "#35506e" : "#26374e"}
                      strokeWidth={activeEdge ? 2.6 : 1.4}
                      strokeOpacity={activeEdge ? 1 : ownedEdge ? 0.5 : 0.36}
                    />
                  );
                });
              })}
            </svg>

            {UPGRADES.map((n) => {
              if (hiddenOf(n)) return null;
              const state = nodeState(n, owned);
              const buyable = buyableOf(n);
              const ic = nodeIcon(n.id);
              const cls = [
                "upg-node",
                `is-${state}`,
                n.milestone ? "is-milestone" : "",
                buyable ? "is-buyable" : "",
                selectedId === n.id ? "is-selected" : "",
                highlightId && n.id === highlightId ? "tut-node-pulse" : "",
              ]
                .filter(Boolean)
                .join(" ");
              const size = sizeOf(n);
              return (
                <button
                  key={n.id}
                  className={cls}
                  style={{
                    left: PAD + n.col * COL_W + (COL_W - size) / 2,
                    top: top(n),
                    width: size,
                    height: size,
                  }}
                  onClick={() => setSelectedId(n.id)}
                  title={t(n.name)}
                  aria-label={t(n.name)}
                >
                  {ic.img ? (
                    <img className="upg-node-icon" src={ic.img} alt="" draggable={false} />
                  ) : (
                    <span className="upg-node-glyph">{ic.glyph}</span>
                  )}
                  {state === "owned" && <span className="upg-badge owned">✓</span>}
                  {state === "locked" && <span className="upg-badge locked">🔒</span>}
                  {buyable && <span className="upg-badge buy">＋</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="upg-foot">
          {t("Tap an upgrade for details · reach Mk II to unlock drones, then push on to Mk III")}
        </div>

        {selected && (
          <DetailPopup
            node={selected}
            state={nodeState(selected, owned)}
            buyable={buyableOf(selected)}
            icon={nodeIcon(selected.id)}
            minerals={minerals}
            skillPoints={skillPoints}
            units={units}
            energyCores={energyCores}
            atMechanical={atMechanical}
            owned={owned}
            onConfirm={() => {
              onPurchase(selected.id);
              setSelectedId(null);
            }}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </div>
  );
}

interface DetailProps {
  node: UpgradeNode;
  state: ReturnType<typeof nodeState>;
  buyable: boolean;
  icon: { img?: string; glyph?: string };
  minerals: number;
  skillPoints: number;
  units: number;
  energyCores: number;
  atMechanical: boolean;
  owned: Set<string>;
  onConfirm: () => void;
  onClose: () => void;
}

/** Popup card with the node's full details + a Confirm button. Tapping the shaded
 * backdrop (anywhere outside the card) closes it without closing the whole tree. */
function DetailPopup({
  node,
  state,
  buyable,
  icon,
  minerals,
  skillPoints,
  units,
  energyCores,
  atMechanical,
  owned,
  onConfirm,
  onClose,
}: DetailProps) {
  const needMech = node.location === "mechanical" && !atMechanical;
  const missing = node.requires.filter((r) => !owned.has(r));
  const excludedBy = node.excludes?.filter((x) => owned.has(x)) ?? [];
  return (
    <div
      className="upg-detail-backdrop"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className={`upg-detail-card${node.milestone ? " milestone" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t(node.name)}
      >
        <div className="upg-detail-head">
          <div className="upg-detail-icon">
            {icon.img ? (
              <img src={icon.img} alt="" draggable={false} />
            ) : (
              <span className="upg-node-glyph big">{icon.glyph}</span>
            )}
          </div>
          <div className="upg-detail-titles">
            <div className="upg-detail-name">{t(node.name)}</div>
            {node.milestone && <div className="upg-detail-tag">★ {t("Milestone")}</div>}
          </div>
          <button className="upg-detail-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="upg-detail-desc">{t(node.desc)}</div>

        <div className="upg-detail-cost">
          <span className={minerals >= node.cost ? "ok" : "no"}>
            <span className="upg-min-dot" /> {fmt(node.cost)}
          </span>
          {node.sp ? (
            <span className={skillPoints >= node.sp ? "ok" : "no"}>★ {node.sp}</span>
          ) : null}
          {node.units ? (
            <span className={units >= node.units ? "ok" : "no"}>◆ {fmt(node.units)}</span>
          ) : null}
          {node.cores ? (
            <span className={energyCores >= node.cores ? "ok" : "no"}>⬢ {node.cores}</span>
          ) : null}
        </div>

        {state === "owned" ? (
          <div className="upg-detail-status owned">✓ {t("Owned")}</div>
        ) : buyable ? (
          <button className="upg-detail-confirm" onClick={onConfirm}>
            {t("Confirm")}
          </button>
        ) : (
          <div className="upg-detail-status locked">
            {excludedBy.length > 0
              ? t("Locked — you chose the other path.")
              : missing.length > 0
                ? `${t("Requires")}: ${missing.map((r) => t(UPGRADES_BY_ID[r]?.name ?? r)).join(", ")}`
                : needMech
                  ? t("🔧 Dock at a mechanical yard to buy.")
                  : t("Not enough resources yet.")}
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}
